import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, CURRENCIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Target, Plus, Trash2, Flag, TrendingUp, Zap } from "lucide-react";
import GoalRulesDialog from "@/components/GoalRulesDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

function GoalDialog({ open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [amount, setAmount] = useState(10000);
  const [currency, setCurrency] = useState(user?.base_currency || "USD");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setAmount(10000); setCurrency(user?.base_currency || "USD"); }
  }, [open, user]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Ponle un nombre");
    setSaving(true);
    try {
      await api.post("/goals", { name, target_amount: Number(amount), currency });
      toast.success("Meta creada");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nueva meta de ahorro</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div>
            <label className="text-sm font-medium text-[#0A110E]">Nombre</label>
            <Input data-testid="goal-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Colchón 6 meses, Entrada coche…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#0A110E]">Objetivo</label>
              <Input data-testid="goal-amount" type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0A110E]">Moneda</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="goal-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="goal-save-btn" onClick={submit} disabled={saving} className="bg-[#0F291E] hover:bg-[#1C7C54]">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalRow({ g, onDelete, onOpenRules }) {
  // Prefer contributed-based progress (per-goal) when there is any
  // contribution; fall back to account-based progress for old goals.
  const contributed = Number(g.contributed_amount || 0);
  const usesContrib = contributed > 0;
  const displayAmount = usesContrib ? contributed : g.current_amount;
  const displayPct = usesContrib ? g.contributed_pct : g.progress_pct;
  const pct = Math.min(100, displayPct || 0);
  return (
    <div className="rounded-xl border border-[#EBEBEA] p-4" data-testid={`goal-${g.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-display text-lg font-semibold text-[#0A110E]">{g.name}</div>
          <div className="text-xs text-[#8A9992] flex items-center gap-2">
            <Flag className="w-3 h-3" />
            <span className="tabular-nums">{formatCurrency(displayAmount, g.currency)} / {formatCurrency(g.target_amount, g.currency)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onOpenRules(g)}
            data-testid={`automate-goal-${g.id}`}
            title="Aportaciones automáticas"
            className="p-2 rounded-lg hover:bg-[#EBEBEA]"
          >
            <Zap className="w-4 h-4 text-[#1C7C54]" />
          </button>
          <button onClick={() => onDelete(g.id)} data-testid={`del-goal-${g.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]">
            <Trash2 className="w-4 h-4 text-[#D95D39]" />
          </button>
        </div>
      </div>
      <div className="w-full h-2 rounded-full bg-[#EBEBEA] overflow-hidden">
        <div className="h-full bg-[#1C7C54] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-[#5F7368] tabular-nums flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-[#1C7C54]" />
          Ritmo actual: {formatCurrency(g.monthly_pace || 0, g.currency)}/mes
        </span>
        <span className="font-semibold text-[#1C7C54] tabular-nums">
          {g.months_at_pace ? `≈ ${g.months_at_pace} meses` : "Ahorra para verlo"} · {pct.toFixed(1)}%
        </span>
      </div>
      {usesContrib && g.contributions_count > 0 && (
        <div className="mt-2 text-[11px] text-[#5F7368] flex items-center gap-1">
          <Zap className="w-3 h-3 text-[#1C7C54]" />
          {g.contributions_count} {g.contributions_count === 1 ? "aportación" : "aportaciones"}
          {g.contributed_auto > 0 && ` · ${formatCurrency(g.contributed_auto, g.currency)} automáticas`}
        </div>
      )}
    </div>
  );
}

export default function Goals() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [rulesGoal, setRulesGoal] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/goals")).data); }
    catch (e) { logger.error("goals load", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/goals/${id}`); toast.success("Meta eliminada"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Flag className="w-3 h-3" /> {t("goals.kickerFlag", { defaultValue: "Metas" })}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("goals.title")}</h1>
          <p className="text-[#5F7368] mt-2">{t("goals.subtitle", { defaultValue: "Define objetivos y descubre a qué ritmo llegas a ellos." })}</p>
        </div>
        <Button data-testid="add-goal-btn" onClick={() => setOpen(true)} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("goals.newGoal")}
        </Button>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-[#5F7368]">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <Target className="w-6 h-6 text-[#1C7C54] mx-auto mb-3" />
          <p className="text-[#8A9992] mb-4">Aún no tienes metas.</p>
          <Button onClick={() => setOpen(true)} className="bg-[#0F291E] hover:bg-[#1C7C54]"><Plus className="w-4 h-4 mr-2" /> Crea la primera</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((g) => <GoalRow key={g.id} g={g} onDelete={remove} onOpenRules={setRulesGoal} />)}
        </div>
      )}

      <GoalDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <GoalRulesDialog
        open={!!rulesGoal}
        onOpenChange={(v) => !v && setRulesGoal(null)}
        goal={rulesGoal}
        onRefresh={load}
      />
    </div>
  );
}

export function GoalsCard() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/goals").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (items === null) return null;
  if (items.length === 0) return null;
  const top = items[0];
  const pct = Math.min(100, top.progress_pct || 0);
  return (
    <div className="card-surface p-6" data-testid="goals-card">
      <div className="flex items-center gap-2 mb-3">
        <Flag className="w-4 h-4 text-[#1C7C54]" />
        <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Meta de ahorro</p>
      </div>
      <div className="font-display text-lg font-semibold text-[#0A110E]">{top.name}</div>
      <div className="text-xs text-[#8A9992] tabular-nums">
        {formatCurrency(top.current_amount, top.currency)} / {formatCurrency(top.target_amount, top.currency)}
      </div>
      <div className="w-full h-2 rounded-full bg-[#EBEBEA] mt-2 overflow-hidden">
        <div className="h-full bg-[#1C7C54] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-xs text-[#5F7368] flex items-center justify-between">
        <span>Ritmo {formatCurrency(top.monthly_pace || 0, top.currency)}/mes</span>
        <span className="font-semibold text-[#1C7C54]">
          {top.months_at_pace ? `≈ ${top.months_at_pace} meses` : "—"}
        </span>
      </div>
    </div>
  );
}
